import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { submitContactLead } from "@/services/contactApi";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./WebsiteCare.css";
import {
  FIELD_LIMITS,
  emailError,
  firstInvalidName,
  requiredText,
  selectError
} from "@/lib/formValidation";

/**
 * Display strings for the three support plans.
 * Leave a value empty until pricing is finalized.
 * Example when ready: essential: "$149 / month"
 */
const PLAN_PRICES = {
  essential: "",
  growth: "",
  partner: ""
};

const PRICE_UNSET = "Pricing to be confirmed";

const carePath = [
  ["Existing Website", "Start with the site you already have, whether BizLogic built it or someone else did."],
  ["Content", "Text, images, and page edits as the business changes."],
  ["Technical Support", "Help when a page, form, or feature needs attention."],
  ["Improvements", "Design and functionality changes on the current website."],
  ["Ongoing Care", "A continuing relationship so the site stays current."]
];

const handles = [
  ["01", "content", "Content Updates", "Text changes, image updates, page edits, and ongoing website content changes."],
  ["02", "maintain", "Website Maintenance", "Routine updates and general website upkeep."],
  ["03", "fix", "Bug Fixes", "Troubleshooting and resolving website issues."],
  ["04", "design", "Design Improvements", "Small visual updates and improvements to existing pages."],
  ["05", "support", "Technical Support", "Help when something stops working or needs attention."],
  ["06", "enhance", "Website Enhancements", "New sections, functionality, integrations, and improvements as your business evolves."]
];

const plans = [
  {
    id: "essential",
    name: "Essential",
    summary: "For businesses that need occasional website updates and maintenance.",
    includes: ["Content updates", "Basic maintenance", "Minor fixes", "Website support"]
  },
  {
    id: "growth",
    name: "Growth",
    summary: "For businesses that need regular website support and improvements.",
    includes: ["Everything in Essential", "More frequent updates", "Design improvements", "Technical support", "Website enhancements"]
  },
  {
    id: "partner",
    name: "Partner",
    summary: "For businesses looking for ongoing website support.",
    includes: ["Everything in Growth", "Ongoing website improvements", "Priority support", "Continuous maintenance", "Custom support needs"]
  }
];

const needs = [
  ["Website already built?", "Care starts from the site you have, not from a blank project."],
  ["Need regular updates?", "Content and page changes can continue after launch."],
  ["Need technical help?", "Fixes and support when something on the site needs attention."],
  ["Need something new added?", "Sections, functions, and integrations can be added to the current site."]
];

const process = [
  ["Tell Us What You Need", "Share the update, fix, or improvement you want on the current website."],
  ["We Review It", "We look at the existing site and what the request actually involves."],
  ["We Make the Changes", "The work happens on the website you already have."],
  ["You Review", "You check the result before it stays in place."],
  ["We Keep Things Moving", "Support continues as new updates and improvements come up."]
];

const elsewhere = [
  ["Your Existing Website", "The site as it is today, including how it was built and what is already published."],
  ["Review", "We look at the current setup and how we can work with it."],
  ["Support", "Updates, fixes, and maintenance on that existing website."],
  ["Improvements", "Changes that move the current site forward."]
];

const helpOptions = [
  "Content Updates",
  "Maintenance",
  "Bug Fix",
  "Design Changes",
  "New Feature",
  "Performance",
  "General Support",
  "Other"
];

const supportTypes = ["One-Time Help", "Monthly Support", "Not Sure Yet"];

const faqs = [
  ["Can you maintain a website you didn't build?", "Yes. If another developer or agency built the site, we can review the current setup and take on updates, fixes, and ongoing care when that setup can be worked with."],
  ["Can I request one-time website changes?", "Yes. One-time help is available when you need a specific change and do not want a monthly plan."],
  ["What types of updates can you handle?", "Content changes, routine maintenance, bug fixes, small design updates, technical support, and enhancements such as new sections or integrations. The exact work depends on the current website."],
  ["Can I switch support plans later?", "Yes. You can start with the level of support that fits now and move to a different plan as the website's needs change."],
  ["Can you add new features to my existing website?", "Yes, when the current site can support the addition. We look at the existing setup first, then add the section, function, or integration."],
  ["Can I use monthly support without rebuilding my website?", "Yes. Monthly support is for the website you already have. A rebuild is a separate project, not a requirement for care."]
];

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  businessName: "",
  websiteUrl: "",
  helpWith: "",
  supportType: "",
  message: ""
};

function useInView(threshold = 0.22) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, shown];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, shown] = useInView();
  return (
    <div ref={ref} className={`wc-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Kicker({ index, label }) {
  return (
    <p className="wc-kicker">
      <span>{index}</span>
      <span>{label}</span>
    </p>
  );
}

function scrollToId(id) {
  const node = document.getElementById(id);
  if (!node) return;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  node.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
}

function CareBrowser() {
  const statuses = [
    ["Website", "Healthy"],
    ["Content", "Updated"],
    ["Security", "Checked"],
    ["Performance", "Reviewed"]
  ];

  return (
    <div className="wc-browser" aria-hidden="true">
      <div className="wc-browser-bar">
        <span />
        <span />
        <span />
        <div>yourwebsite.com</div>
      </div>
      <div className="wc-browser-body">
        <div className="wc-pageui">
          <div className="wc-pageui-nav">
            <span className="wc-pageui-logo" />
            <span className="wc-pageui-links"><i /><i /><i /></span>
            <span className="wc-pageui-live"><i /></span>
          </div>
          <div className="wc-pageui-layout">
            <div className="wc-pageui-feature">
              <div className="wc-care-board">
                <div className="wc-care-board-head">
                  <span>Website</span>
                  <strong><i />Updated</strong>
                </div>
                <div className="wc-care-screen">
                  <header>
                    <b>Homepage</b>
                    <span>Content</span>
                  </header>
                  <div className="wc-care-blocks">
                    <span>
                      <svg viewBox="0 0 24 24" fill="none">
                        <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M4 9h16" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M8 13h5M8 16h3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                      <em>Pages</em>
                    </span>
                    <span>
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M6.5 8.5A6.2 6.2 0 0 1 17 7.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M17 4.8v2.7h-2.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M17.5 15.5A6.2 6.2 0 0 1 7 16.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M7 19.2v-2.7h2.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <em>Updates</em>
                    </span>
                  </div>
                </div>
                <span className="wc-care-review">Review</span>
              </div>
            </div>
            <div className="wc-pageui-rail">
              <div className="wc-care-task">
                <span className="wc-care-ico">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M7 3.5h7.2L19 8.2V20.5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <path d="M14 3.8V8.4h4.4M9 12.5h6M9 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </span>
                <span>
                  <strong>Content</strong>
                  <em>Updated</em>
                </span>
                <i />
              </div>
              <div className="wc-care-task">
                <span className="wc-care-ico">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 3.5l7 2.4v6.2c0 4.2-2.8 7.2-7 8.4-4.2-1.2-7-4.2-7-8.4V5.9l7-2.4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <path d="M9.2 12.1l1.8 1.8 3.8-3.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span>
                  <strong>Security</strong>
                  <em>Checked</em>
                </span>
                <i />
              </div>
              <div className="wc-care-task">
                <span className="wc-care-ico">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M3.5 12h3.2l2-4.5 3.2 9 2.1-4.5H20.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span>
                  <strong>Performance</strong>
                  <em>Reviewed</em>
                </span>
                <i />
              </div>
            </div>
          </div>
        </div>
        <ul className="wc-status">
          {statuses.map(([label, state], index) => (
            <li key={label} style={{ animationDelay: `${700 + index * 90}ms` }}>
              <span>{label}</span>
              <strong>{state}</strong>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function CarePath() {
  const [active, setActive] = useState(0);
  const [title, note] = carePath[active];

  return (
    <div className="wc-path-layout">
      <div className="wc-path" role="list">
        {carePath.map(([label], index) => (
          <button
            key={label}
            type="button"
            role="listitem"
            className={`wc-path-step${active === index ? " is-on" : ""}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
            onMouseEnter={() => setActive(index)}
          >
            <em>{String(index + 1).padStart(2, "0")}</em>
            <span>{label}</span>
          </button>
        ))}
      </div>
      <article className="wc-path-detail" aria-live="polite">
        <p>{String(active + 1).padStart(2, "0")}</p>
        <h3>{title}</h3>
        <p>{note}</p>
      </article>
    </div>
  );
}

function CapMark({ type }) {
  if (type === "content") {
    return (
      <span className="wc-mark wc-mark-content" aria-hidden="true">
        <i /><i /><i />
      </span>
    );
  }
  if (type === "maintain") {
    return (
      <span className="wc-mark wc-mark-maintain" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="2.5" y="3.75" width="12.5" height="9.5" rx="1.75" stroke="#2563eb" strokeWidth="1.6" />
          <path d="M2.5 6.9h12.5" stroke="#2563eb" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M16.15 14.2a3.55 3.55 0 1 1-2.15 4.05" stroke="#2563eb" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M16.15 12.15v2.35h2.35" stroke="#2563eb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (type === "fix") {
    return (
      <span className="wc-mark wc-mark-fix" aria-hidden="true">
        <i /><b />
      </span>
    );
  }
  if (type === "design") {
    return (
      <span className="wc-mark wc-mark-design" aria-hidden="true">
        <i /><b />
      </span>
    );
  }
  if (type === "support") {
    return (
      <span className="wc-mark wc-mark-support" aria-hidden="true">
        <i />
      </span>
    );
  }
  return (
    <span className="wc-mark wc-mark-enhance" aria-hidden="true">
      <i /><i /><i />
    </span>
  );
}

function ReviewBoard() {
  return (
    <div className="wc-review" aria-hidden="true">
      <div className="wc-review-bar">
        <span>Existing website</span>
        <strong>In review</strong>
      </div>
      <div className="wc-review-stage">
        <div className="wc-review-page">
          <div className="wc-preview">
            <div className="wc-preview-nav">
              <span className="wc-preview-logo" />
              <span className="wc-preview-links">
                <em>Home</em>
                <em>Services</em>
                <em className="wc-site-hide">About</em>
              </span>
              <span className="wc-preview-live"><i /></span>
            </div>
            <div className="wc-preview-feature">
              <div className="wc-site">
                <div className="wc-site-copy">
                  <span>Welcome</span>
                  <strong>Your website</strong>
                  <p>Open for review.</p>
                </div>
                <div className="wc-site-list">
                  <span>
                    <i>
                      <svg viewBox="0 0 24 24" fill="none">
                        <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </i>
                    <span>
                      <b>Services</b>
                      <em>Client work</em>
                    </span>
                    <strong>Open</strong>
                  </span>
                  <span>
                    <i>
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M6.5 8.5A6.2 6.2 0 0 1 17 7.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M17 4.8v2.7h-2.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M17.5 15.5A6.2 6.2 0 0 1 7 16.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M7 19.2v-2.7h2.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </i>
                    <span>
                      <b>Updates</b>
                      <em>Latest pages</em>
                    </span>
                    <strong>3</strong>
                  </span>
                  <span>
                    <i>
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M5 7.5h14v9H5z" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M5 7.5l7 5.5 7-5.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                      </svg>
                    </i>
                    <span>
                      <b>Contact</b>
                      <em>Ready to reply</em>
                    </span>
                    <strong>Live</strong>
                  </span>
                </div>
                <div className="wc-site-foot">
                  <span><i />Live</span>
                  <em>View page</em>
                </div>
              </div>
            </div>
            <div className="wc-preview-row">
              <span className="wc-preview-card">
                <i>
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M6 18.5c1.2-2.4 3-3.6 6-3.6s4.8 1.2 6 3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </i>
                <em>Team</em>
              </span>
              <span className="wc-preview-card is-alt">
                <i>
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 7.5h14v9H5z" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M5 7.5l7 5.5 7-5.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  </svg>
                </i>
                <em>Contact</em>
              </span>
            </div>
            <span className="wc-preview-btn">Visit</span>
          </div>
        </div>
        <ul>
          <li><span>Content</span><strong>Noted</strong></li>
          <li><span>Layout</span><strong>Reviewed</strong></li>
          <li><span>Pages</span><strong>Open</strong></li>
        </ul>
      </div>
    </div>
  );
}

function ProcessViz({ index }) {
  if (index === 0) {
    return (
      <div className="wc-viz wc-viz-note">
        <small>Request</small>
        <strong>Update the services page</strong>
        <span>Page</span>
        <em>Services</em>
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="wc-viz wc-viz-review">
        <div><small>Site</small><strong>Current website</strong></div>
        <i aria-hidden="true" />
        <div className="is-on"><small>Review</small><strong>What the change needs</strong></div>
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="wc-viz wc-viz-change">
        <div><small>Before</small><b /><b /><b className="is-short" /></div>
        <div className="is-on"><small>After</small><b /><b /><b /></div>
      </div>
    );
  }
  if (index === 3) {
    return (
      <div className="wc-viz wc-viz-check">
        <span>Ready for review</span>
        <strong>You confirm the change</strong>
      </div>
    );
  }
  return (
    <div className="wc-viz wc-viz-cycle">
      {["Update", "Fix", "Improve"].map((label) => (
        <span key={label}>{label}</span>
      ))}
    </div>
  );
}

function ProcessTimeline() {
  const [active, setActive] = useState(0);
  const [title, text] = process[active];

  return (
    <>
      <div className="wc-process">
        {process.map(([stepTitle, stepText], index) => (
          <button
            key={stepTitle}
            type="button"
            className={`wc-step${active === index ? " is-active" : ""}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
          >
            <span className="wc-step-mark">{String(index + 1).padStart(2, "0")}</span>
            <span className="wc-step-copy">
              <span>{stepTitle}</span>
              <span>{stepText}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="wc-stage" aria-live="polite">
        <div className="wc-stage-body" key={active}>
          <p>{String(active + 1).padStart(2, "0")}</p>
          <h3>{title}</h3>
          <p>{text}</p>
          <ProcessViz index={active} />
        </div>
      </div>
    </>
  );
}

function ElsewhereBoard() {
  const [active, setActive] = useState(1);
  const [title, note] = elsewhere[active];

  return (
    <div className="wc-else">
      <div className="wc-else-site" aria-hidden="true">
        <div className="wc-else-bar"><span /><span /><span /></div>
        <div className="wc-else-page">
          <div className="wc-mini-nav">
            <span className="wc-mini-logo" />
            <span>Home</span>
            <span>Services</span>
            <span className="wc-mini-hide">About</span>
            <span className="wc-mini-cta">Contact</span>
          </div>
          <div className="wc-mini-copy">
            <span className="wc-mini-kicker">Welcome</span>
            <strong>Welcome Back</strong>
            <span className="wc-mini-lead">Business solutions built around your needs.</span>
          </div>
          <div className="wc-mini-cards">
            <span>
              <i />
              <b>Updates</b>
              <em>Pages</em>
            </span>
            <span>
              <i />
              <b>Support</b>
              <em>Care</em>
            </span>
          </div>
          <div className="wc-mini-status">
            <i />
            <span>Site active</span>
          </div>
        </div>
        <p>Your existing website</p>
      </div>
      <div className="wc-else-flow">
        {elsewhere.map(([label], index) => (
          <button
            key={label}
            type="button"
            className={active === index ? "is-on" : ""}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
            onMouseEnter={() => setActive(index)}
          >
            <em>{String(index + 1).padStart(2, "0")}</em>
            <span>{label}</span>
          </button>
        ))}
        <article aria-live="polite">
          <h3>{title}</h3>
          <p>{note}</p>
        </article>
      </div>
    </div>
  );
}

function FaqList() {
  const [open, setOpen] = useState(null);
  return (
    <div className="wc-faq">
      {faqs.map(([question, answer], index) => {
        const isOpen = open === index;
        return (
          <div key={question} className={`wc-acc${isOpen ? " is-open" : ""}`}>
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : index)}
            >
              <span>{question}</span>
              <span className="wc-plus" aria-hidden="true"><i /><i /></span>
            </button>
            <div className={`wc-acc-panel${isOpen ? " is-open" : ""}`}>
              <div>
                <p>{answer}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function careFieldError(name, value) {
  if (name === "firstName") return requiredText(value, "Please enter your first name.");
  if (name === "lastName") return requiredText(value, "Please enter your last name.");
  if (name === "email") return emailError(value);
  if (name === "businessName") return requiredText(value, "Please enter your business name.");
  if (name === "websiteUrl") return requiredText(value, "Please enter your website.");
  if (name === "helpWith" || name === "supportType") return selectError(value);
  if (name === "message") return requiredText(value, "Please enter your message.");
  return "";
}

const careFieldOrder = [
  "firstName",
  "lastName",
  "email",
  "businessName",
  "websiteUrl",
  "helpWith",
  "supportType",
  "message"
];

function SupportForm({ plan, onPlanClear }) {
  const formRef = useRef(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => (current[name] ? { ...current, [name]: careFieldError(name, value) } : current));
  };

  const blur = (event) => {
    const { name, value, type } = event.target;
    if (type === "radio") {
      const selected = event.target.form?.elements.supportType?.value || "";
      setErrors((current) => ({ ...current, supportType: careFieldError("supportType", selected) }));
      return;
    }
    const next = value.trim();
    setForm((current) => ({ ...current, [name]: next }));
    setErrors((current) => ({ ...current, [name]: careFieldError(name, next) }));
  };

  const submit = async (event) => {
    event.preventDefault();
    const nextForm = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      businessName: form.businessName.trim(),
      websiteUrl: form.websiteUrl.trim(),
      helpWith: form.helpWith.trim(),
      supportType: form.supportType.trim(),
      message: form.message.trim()
    };
    const nextErrors = Object.fromEntries(
      careFieldOrder.map((name) => [name, careFieldError(name, nextForm[name])])
    );
    setForm(nextForm);
    setErrors(nextErrors);
    const invalid = firstInvalidName(nextErrors, careFieldOrder);
    if (invalid) {
      formRef.current?.querySelector(`[name="${invalid}"]`)?.focus();
      return;
    }

    setStatus("sending");
    setError("");

    const details = [
      plan ? `Plan interest: ${plans.find((item) => item.id === plan)?.name || plan}` : null,
      `Help with: ${nextForm.helpWith}`,
      `Website: ${nextForm.websiteUrl}`,
      `Support type: ${nextForm.supportType}`,
      "",
      nextForm.message
    ].filter((line) => line !== null).join("\n");

    try {
      await submitContactLead({
        full_name: `${nextForm.firstName} ${nextForm.lastName}`.trim(),
        email: nextForm.email,
        phone: "Not provided",
        company_name: nextForm.businessName,
        service_needed: "Website Care & Support",
        budget_range: nextForm.supportType,
        project_details: details
      });
      setStatus("sent");
      setForm(emptyForm);
      setErrors({});
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "The request could not be sent. Please try again."
      );
      setStatus("idle");
    }
  };

  if (status === "sent") {
    return (
      <div className="wc-form-success" role="status">
        <p>Request received</p>
        <h3>We have your website support request.</h3>
        <p>We will review what you sent and follow up about the next step.</p>
        <button type="button" onClick={() => { setStatus("idle"); onPlanClear(); }}>
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form ref={formRef} className="wc-form" onSubmit={submit} noValidate>
      {plan ? (
        <p className="wc-plan-chip">
          Plan interest: {plans.find((item) => item.id === plan)?.name}
          <button type="button" onClick={onPlanClear}>Clear</button>
        </p>
      ) : null}
      {error ? <p className="wc-form-error" role="alert">{error}</p> : null}
      <div className="wc-form-grid">
        <div className="wc-field">
          <label htmlFor="care-first">First Name</label>
          <input id="care-first" name="firstName" autoComplete="given-name" value={form.firstName} onChange={update} onBlur={blur} required maxLength={FIELD_LIMITS.name} aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? "care-first-error" : undefined} />
          {errors.firstName ? <p id="care-first-error" className="wc-field-error" role="alert">{errors.firstName}</p> : null}
        </div>
        <div className="wc-field">
          <label htmlFor="care-last">Last Name</label>
          <input id="care-last" name="lastName" autoComplete="family-name" value={form.lastName} onChange={update} onBlur={blur} required maxLength={FIELD_LIMITS.name} aria-invalid={Boolean(errors.lastName)} aria-describedby={errors.lastName ? "care-last-error" : undefined} />
          {errors.lastName ? <p id="care-last-error" className="wc-field-error" role="alert">{errors.lastName}</p> : null}
        </div>
        <div className="wc-field">
          <label htmlFor="care-email">Email</label>
          <input id="care-email" name="email" type="email" autoComplete="email" value={form.email} onChange={update} onBlur={blur} required maxLength={FIELD_LIMITS.email} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "care-email-error" : undefined} />
          {errors.email ? <p id="care-email-error" className="wc-field-error" role="alert">{errors.email}</p> : null}
        </div>
        <div className="wc-field">
          <label htmlFor="care-business">Business Name</label>
          <input id="care-business" name="businessName" autoComplete="organization" value={form.businessName} onChange={update} onBlur={blur} required maxLength={FIELD_LIMITS.company} aria-invalid={Boolean(errors.businessName)} aria-describedby={errors.businessName ? "care-business-error" : undefined} />
          {errors.businessName ? <p id="care-business-error" className="wc-field-error" role="alert">{errors.businessName}</p> : null}
        </div>
        <div className="wc-field wc-field-wide">
          <label htmlFor="care-url">Website URL</label>
          <input id="care-url" name="websiteUrl" type="text" inputMode="url" autoComplete="url" placeholder="https://yourwebsite.com" value={form.websiteUrl} onChange={update} onBlur={blur} required maxLength={FIELD_LIMITS.url} aria-invalid={Boolean(errors.websiteUrl)} aria-describedby={errors.websiteUrl ? "care-url-error" : undefined} />
          {errors.websiteUrl ? <p id="care-url-error" className="wc-field-error" role="alert">{errors.websiteUrl}</p> : null}
        </div>
        <div className="wc-field wc-field-wide">
          <label htmlFor="care-help">What Do You Need Help With?</label>
          <select id="care-help" name="helpWith" value={form.helpWith} onChange={update} onBlur={blur} required aria-invalid={Boolean(errors.helpWith)} aria-describedby={errors.helpWith ? "care-help-error" : undefined}>
            <option value="" disabled>Select one</option>
            {helpOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {errors.helpWith ? <p id="care-help-error" className="wc-field-error" role="alert">{errors.helpWith}</p> : null}
        </div>
      </div>
      <fieldset className="wc-support-type" aria-invalid={Boolean(errors.supportType)} aria-describedby={errors.supportType ? "care-support-error" : undefined}>
        <legend>Support Type</legend>
        <div>
          {supportTypes.map((type) => (
            <label key={type}>
              <input
                type="radio"
                name="supportType"
                value={type}
                checked={form.supportType === type}
                onChange={update}
                onBlur={blur}
                required
              />
              <span>{type}</span>
            </label>
          ))}
        </div>
        {errors.supportType ? <p id="care-support-error" className="wc-field-error" role="alert">{errors.supportType}</p> : null}
      </fieldset>
      <div className="wc-field">
        <label htmlFor="care-message">Message</label>
        <textarea id="care-message" name="message" rows={5} placeholder="Tell us what you need." value={form.message} onChange={update} onBlur={blur} required maxLength={FIELD_LIMITS.message} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? "care-message-error" : undefined} />
        {errors.message ? <p id="care-message-error" className="wc-field-error" role="alert">{errors.message}</p> : null}
      </div>
      <button type="submit" className="wc-submit" disabled={status === "sending"}>
        {status === "sending" ? "Sending request..." : "Request Website Support"}
        {status === "sending" ? null : <ArrowRight aria-hidden="true" />}
      </button>
    </form>
  );
}

export default function WebsiteCare() {
  const [plan, setPlan] = useState("");

  useEffect(() => {
    const previous = document.title;
    document.title = "Website Care | BizLogic Developers";
    return () => {
      document.title = previous;
    };
  }, []);

  const requestPlan = (id) => {
    setPlan(id);
    scrollToId("care-request");
  };

  return (
    <article className="wc-page bg-white" data-testid="website-care-page">
      <header className="wc-hero">
        <div className="wc-wrap wc-hero-grid">
          <div>
            <Link to="/services" className="wc-back wc-hero-item" style={{ animationDelay: "40ms" }}>
              <ArrowLeft aria-hidden="true" />
              All services
            </Link>
            <p className="wc-eyebrow wc-hero-item" style={{ animationDelay: "120ms" }}>Website Care & Support</p>
            <h1 className="wc-hero-item" style={{ animationDelay: "220ms" }}>
              Your Website. Kept Current, Secure, and Ready to Grow.
            </h1>
            <p className="wc-lead wc-hero-item" style={{ animationDelay: "360ms" }}>
              Ongoing website maintenance, content updates, technical support, and improvements for businesses that want a reliable digital partner after launch.
            </p>
            <div className="wc-actions wc-hero-item" style={{ animationDelay: "500ms" }}>
              <button type="button" className="wc-btn wc-btn-primary" onClick={() => scrollToId("care-request")}>
                Request Website Support
                <ArrowRight aria-hidden="true" />
              </button>
              <button type="button" className="wc-btn wc-btn-ghost" onClick={() => scrollToId("support-plans")}>
                View Support Plans
                <ArrowRight aria-hidden="true" />
              </button>
            </div>
          </div>
          <CareBrowser />
        </div>
      </header>

      <section className="wc-section wc-band">
        <div className="wc-wrap">
          <Reveal>
            <Kicker index="02" label="Already have a website?" />
            <h2 className="wc-keep-title">
              You Don&apos;t Need a New Website to Work <span>With BizLogic.</span>
            </h2>
            <p className="wc-lead">
              If you already have a website, we can work with what you have. Whether you need content changes, technical fixes, design improvements, or ongoing support, we can help keep your website moving forward.
            </p>
          </Reveal>
          <div className="wc-after">
            <CarePath />
          </div>
        </div>
      </section>

      <section className="wc-section">
        <div className="wc-wrap">
          <Reveal>
            <Kicker index="03" label="What we handle" />
            <h2 className="wc-phrase-title">
              <span>The Small Things.</span>{" "}
              <span>The Important Things.</span>{" "}
              <span>The Ongoing Things.</span>
            </h2>
            <p className="wc-lead">
              Websites require attention long after launch. We handle the updates and technical work that keep your website useful and current.
            </p>
          </Reveal>
          <div className="wc-caps wc-after">
            {handles.map(([number, type, title, text], index) => (
              <Reveal key={title} delay={(index % 3) * 60} className="h-full">
                <article className={`wc-cap wc-cap-${type}`}>
                  <CapMark type={type} />
                  <span>{number}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="wc-section wc-band" id="support-plans">
        <div className="wc-wrap">
          <Reveal>
            <Kicker index="04" label="Support plans" />
            <h2>Choose the Level of Support Your Website Needs.</h2>
            <p className="wc-lead">
              Start with the level of support that makes sense for your business and adjust as your needs change.
            </p>
          </Reveal>
          <div className="wc-plans wc-after">
            {plans.map((item) => (
              <article key={item.id} className={`wc-plan${item.id === "growth" ? " is-focus" : ""}`}>
                <div>
                  <h3>{item.name}</h3>
                  <p className="wc-price">{PLAN_PRICES[item.id] || PRICE_UNSET}</p>
                  <p>{item.summary}</p>
                </div>
                <ul>
                  {item.includes.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <button type="button" onClick={() => requestPlan(item.id)}>
                  Request Plan
                  <ArrowRight aria-hidden="true" />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="wc-section">
        <div className="wc-wrap wc-split">
          <ReviewBoard />
          <Reveal>
            <Kicker index="05" label="Have a different need?" />
            <h2>Already Have a Website? Let&apos;s Talk About What It Needs.</h2>
            <p className="wc-lead">
              Not every website fits neatly into a monthly plan. Tell us what you have, what needs attention, and what you want to improve.
            </p>
            <ul className="wc-needs">
              {needs.map(([title, text]) => (
                <li key={title}>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <button type="button" className="wc-btn wc-btn-primary" onClick={() => scrollToId("care-request")}>
              Tell Us About Your Website
              <ArrowRight aria-hidden="true" />
            </button>
          </Reveal>
        </div>
      </section>

      <section className="wc-section wc-work wc-band">
        <div className="wc-wrap">
          <div className="wc-work-intro">
            <Reveal>
              <Kicker index="06" label="How it works" />
              <h2>Simple Support. No Complicated Process.</h2>
            </Reveal>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="wc-section">
        <div className="wc-wrap">
          <Reveal>
            <Kicker index="07" label="Built somewhere else?" />
            <h2>Your Website Doesn&apos;t Have to Be Built by BizLogic.</h2>
            <p className="wc-lead">
              If another developer or agency originally built your website, that&apos;s okay. We can review the existing setup and determine how we can help.
            </p>
          </Reveal>
          <div className="wc-after">
            <ElsewhereBoard />
          </div>
        </div>
      </section>

      <section className="wc-section wc-band" id="care-request">
        <div className="wc-wrap">
          <Reveal>
            <Kicker index="08" label="Let's talk" />
            <h2>Tell Us What Your Website Needs.</h2>
            <p className="wc-lead">
              Whether you need a few updates or ongoing support, send us the details and we&apos;ll take a look.
            </p>
          </Reveal>
          <div className="wc-after">
            <SupportForm plan={plan} onPlanClear={() => setPlan("")} />
          </div>
        </div>
      </section>

      <section className="wc-section">
        <div className="wc-wrap">
          <Reveal>
            <Kicker index="09" label="FAQ" />
            <h2>Questions About Website Care.</h2>
          </Reveal>
          <div className="wc-after">
            <FaqList />
          </div>
        </div>
      </section>

      <section className="wc-cta">
        <div className="wc-cta-grid" aria-hidden="true" />
        <div className="wc-cta-glow" aria-hidden="true" />
        <div className="wc-wrap wc-cta-inner">
          <h2>Keep Your Website Moving Forward.</h2>
          <p className="wc-lead">
            Your website should continue working for your business long after launch.
          </p>
          <button type="button" className="wc-btn wc-btn-primary" onClick={() => scrollToId("care-request")}>
            Request Website Support
            <ArrowRight aria-hidden="true" />
          </button>
        </div>
      </section>
    </article>
  );
}
